import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { lastMonthFormatted, lastYearLastMonthFormatted } from "../util/getDate"
import { CO2_FACTOR } from "../util/Factor"
import axios from "axios"

const Wrapper = styled.div`
  display: flex;
  width: 392px;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`

const CardFrame = styled.div`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 16px;
`

const Item = styled.div`
  display: flex;
  min-width: 360px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`

const TitleBadge = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  color: var(--base-gray100, #262626);
  font-family: SUIT;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`

const Badge = styled.div`
  display: flex;
  padding: 6px 12px;
  justify-content: center;
  align-items: center;
  border-radius: 99px;
  border: 1px solid var(--base-gray40, #bfbfbf);
`

const GraphWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  align-self: stretch;
`

const GraphItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`

const GraphHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  align-self: stretch;
`

const DateWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  color: var(--base-gray100, #262626);
  font-family: SUIT;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

const Value = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  color: var(--base-gray100, #262626);
  font-family: SUIT;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

interface GraphProps {
  widthPercent: number // 퍼센트 값을 받아 width에 적용
}

const Graph = styled.div<GraphProps>`
  height: 16px;
  border-radius: 99px;
  background: var(--purple40, #b89efa);
  width: ${(props) => props.widthPercent}%;
`

const Graph2 = styled.div<GraphProps>`
  height: 16px;
  border-radius: 99px;
  background: var(--secondary60, #689cf6);
  width: ${(props) => props.widthPercent}%;
`

interface DashBoardMonthData {
  previousMonthGeneration: number // 저번 달 생산량
  previousMonthConsumption: number // 저번 달 소비량
  lastYearPreviousMonthGeneration: number // 1년 전 저번 달 생산량
  lastYearPreviousMonthConsumption: number // 1년 전 저번 달 소비량
  previousMonthReductions: number // 저번달 온실가스 감축량
  lastYearPreviousMonthReductions: number // 1년 전 저번 달 온실가스 감축량
}

const ComparisonGraph = () => {
  const [data, setData] = useState<DashBoardMonthData>({
    previousMonthGeneration: 0,
    previousMonthConsumption: 0,
    lastYearPreviousMonthGeneration: 0,
    lastYearPreviousMonthConsumption: 0,
    previousMonthReductions: 0,
    lastYearPreviousMonthReductions: 0,
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/smart-farm/dashboard-month`
        )
        const {
          previousMonthGeneration,
          previousMonthConsumption,
          lastYearPreviousMonthGeneration,
          lastYearPreviousMonthConsumption,
        } = response.data

        const previousMonthReductions =
          (previousMonthGeneration / 1000) * CO2_FACTOR

        const lastYearPreviousMonthReductions =
          (lastYearPreviousMonthGeneration / 1000) * CO2_FACTOR

        setData({
          previousMonthGeneration: previousMonthGeneration / 1000,
          previousMonthConsumption: previousMonthConsumption / 1000,
          lastYearPreviousMonthGeneration:
            lastYearPreviousMonthGeneration / 1000,
          lastYearPreviousMonthConsumption:
            lastYearPreviousMonthConsumption / 1000,
          previousMonthReductions,
          lastYearPreviousMonthReductions,
        })
      } catch (error) {
        console.error("Error fetching dashboard data:", error)
      }
    }

    fetchData()
  }, [])

  const getWidthPercent = (value: number, maxValue: number): number => {
    if (maxValue === 0) return 0
    return (value / maxValue) * 100
  }

  const consumptionMax = Math.max(
    data.previousMonthConsumption,
    data.lastYearPreviousMonthConsumption
  )

  const generationMax = Math.max(
    data.previousMonthGeneration,
    data.lastYearPreviousMonthGeneration
  )

  const reductionMax = Math.max(
    data.previousMonthReductions,
    data.lastYearPreviousMonthReductions
  )

  return (
    <Wrapper>
      <CardFrame>
        <Item>
          <TitleBadge>
            <Title>
              <svg
                width="19"
                height="25"
                viewBox="0 0 19 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.06589 1.49669C4.27655 0.899075 4.8364 0.5 5.46412 0.5H13.1274C14.1406 0.5 14.856 1.50311 14.5356 2.47446L12.8119 7.70044H17.5127C18.8238 7.70044 19.4911 9.29273 18.5788 10.2444L5.44446 23.9447C4.1932 25.2499 2.03418 24.0721 2.42711 22.2987L3.93425 15.4966L2.0718 15.4894C0.638855 15.4839 -0.358641 14.0488 0.122237 12.6845L4.06589 1.49669Z"
                  fill="#FCB251"
                />
              </svg>
              에너지 사용
            </Title>
            <Badge>참여농장 4SITE</Badge>
            {/* <Badge>참여지역 3 SITE</Badge> */}
          </TitleBadge>
          <GraphWrapper>
            <GraphItem>
              <GraphHeader>
                <DateWrapper>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 4.06C13.93 2.73 12.81 1.75 11.48 1.75H11.2V0.7C11.2 0.28 10.92 0 10.5 0C10.08 0 9.8 0.28 9.8 0.7V1.75H4.2V0.7C4.2 0.28 3.92 0 3.5 0C3.08 0 2.8 0.28 2.8 0.7V1.75H2.52H2.38C1.05 1.82 0 2.94 0 4.27V5.67V5.95V11.55C0 12.88 1.12 14 2.45 14H11.55C12.88 14 14 12.88 14 11.55V5.95V5.67V4.27V4.06ZM12.95 11.55C12.95 12.32 12.32 12.95 11.55 12.95H2.45C1.68 12.95 1.05 12.32 1.05 11.55V5.95H12.95V11.55Z"
                      fill="#8C8C8C"
                    />
                  </svg>
                  {lastMonthFormatted}
                </DateWrapper>
                <Value>
                  <svg
                    width="11"
                    height="14"
                    viewBox="0 0 11 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.35394 0.5814C2.47589 0.232794 2.80002 0 3.16344 0H7.6001C8.18665 0 8.60082 0.585145 8.41534 1.15177L7.41743 4.20026H10.1389C10.898 4.20026 11.2843 5.12909 10.7561 5.68421L3.15205 13.6761C2.42764 14.4374 1.17768 13.7504 1.40517 12.7159L2.27772 8.74804L1.19946 8.74383C0.369863 8.74059 -0.207634 7.90344 0.0707689 7.10765L2.35394 0.5814Z"
                      fill="#8C8C8C"
                    />
                  </svg>
                  {Number(
                    data.previousMonthConsumption.toFixed(0)
                  ).toLocaleString() || 0}{" "}
                  MWh
                </Value>
              </GraphHeader>
              <Graph
                widthPercent={getWidthPercent(
                  data.previousMonthConsumption,
                  consumptionMax
                )}
              />
            </GraphItem>
            <GraphItem>
              <GraphHeader>
                <DateWrapper>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 4.06C13.93 2.73 12.81 1.75 11.48 1.75H11.2V0.7C11.2 0.28 10.92 0 10.5 0C10.08 0 9.8 0.28 9.8 0.7V1.75H4.2V0.7C4.2 0.28 3.92 0 3.5 0C3.08 0 2.8 0.28 2.8 0.7V1.75H2.52H2.38C1.05 1.82 0 2.94 0 4.27V5.67V5.95V11.55C0 12.88 1.12 14 2.45 14H11.55C12.88 14 14 12.88 14 11.55V5.95V5.67V4.27V4.06ZM12.95 11.55C12.95 12.32 12.32 12.95 11.55 12.95H2.45C1.68 12.95 1.05 12.32 1.05 11.55V5.95H12.95V11.55Z"
                      fill="#8C8C8C"
                    />
                  </svg>
                  {lastYearLastMonthFormatted}
                </DateWrapper>
                <Value>
                  <svg
                    width="11"
                    height="14"
                    viewBox="0 0 11 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.35394 0.5814C2.47589 0.232794 2.80002 0 3.16344 0H7.6001C8.18665 0 8.60082 0.585145 8.41534 1.15177L7.41743 4.20026H10.1389C10.898 4.20026 11.2843 5.12909 10.7561 5.68421L3.15205 13.6761C2.42764 14.4374 1.17768 13.7504 1.40517 12.7159L2.27772 8.74804L1.19946 8.74383C0.369863 8.74059 -0.207634 7.90344 0.0707689 7.10765L2.35394 0.5814Z"
                      fill="#8C8C8C"
                    />
                  </svg>
                  {Number(
                    data.lastYearPreviousMonthConsumption.toFixed(0)
                  ).toLocaleString() || 0}{" "}
                  MWh
                </Value>
              </GraphHeader>
              <Graph2
                widthPercent={getWidthPercent(
                  data.lastYearPreviousMonthConsumption,
                  consumptionMax
                )}
              />
            </GraphItem>
          </GraphWrapper>
        </Item>
      </CardFrame>

      <CardFrame>
        <Item>
          <TitleBadge>
            <Title>
              <svg
                width="18"
                height="25"
                viewBox="0 0 18 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.24499 1.21796C8.21426 0.260679 9.78574 0.260681 10.755 1.21796L15.364 5.76994C15.8033 6.20385 16.1877 6.67146 16.5172 7.16434C18.8238 10.6145 18.4393 15.3032 15.364 18.3405C15.3401 18.3641 15.3161 18.3875 15.292 18.4108C15.1235 18.5736 14.9501 18.7286 14.7723 18.8757C13.3497 20.0528 11.6455 20.7275 9.9 20.8997V23.6111C9.9 24.102 9.49706 24.5 9 24.5C8.50294 24.5 8.1 24.102 8.1 23.6111V20.8997C6.60388 20.7521 5.13805 20.2353 3.85395 19.3494C3.42592 19.0541 3.01808 18.7178 2.63604 18.3405C0.870679 16.597 -0.00798276 14.3093 5.46408e-05 12.0242C0.00201846 11.4658 0.0569177 10.9076 0.164752 10.3575C0.316446 9.58373 0.572891 8.82599 0.934089 8.10652C1.35748 7.26317 1.92479 6.47239 2.63604 5.76994L7.24499 1.21796ZM8.1 19.1108C8.69747 19.1847 9.30253 19.1847 9.9 19.1108V11.1669C9.9 10.676 9.49706 10.278 9 10.278C8.50294 10.278 8.1 10.676 8.1 11.1669V19.1108Z"
                  fill="#67BD62"
                />
              </svg>
              재생에너지 발전
            </Title>
            {/* 정읍이 50 */}
            <Badge>총 설치용량 375kW</Badge>
            {/* <Badge>총 설치용량 325kW</Badge> */}
          </TitleBadge>
          <GraphWrapper>
            <GraphItem>
              <GraphHeader>
                <DateWrapper>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 4.06C13.93 2.73 12.81 1.75 11.48 1.75H11.2V0.7C11.2 0.28 10.92 0 10.5 0C10.08 0 9.8 0.28 9.8 0.7V1.75H4.2V0.7C4.2 0.28 3.92 0 3.5 0C3.08 0 2.8 0.28 2.8 0.7V1.75H2.52H2.38C1.05 1.82 0 2.94 0 4.27V5.67V5.95V11.55C0 12.88 1.12 14 2.45 14H11.55C12.88 14 14 12.88 14 11.55V5.95V5.67V4.27V4.06ZM12.95 11.55C12.95 12.32 12.32 12.95 11.55 12.95H2.45C1.68 12.95 1.05 12.32 1.05 11.55V5.95H12.95V11.55Z"
                      fill="#8C8C8C"
                    />
                  </svg>
                  {lastMonthFormatted}
                </DateWrapper>
                <Value>
                  <svg
                    width="11"
                    height="14"
                    viewBox="0 0 11 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.35394 0.5814C2.47589 0.232794 2.80002 0 3.16344 0H7.6001C8.18665 0 8.60082 0.585145 8.41534 1.15177L7.41743 4.20026H10.1389C10.898 4.20026 11.2843 5.12909 10.7561 5.68421L3.15205 13.6761C2.42764 14.4374 1.17768 13.7504 1.40517 12.7159L2.27772 8.74804L1.19946 8.74383C0.369863 8.74059 -0.207634 7.90344 0.0707689 7.10765L2.35394 0.5814Z"
                      fill="#8C8C8C"
                    />
                  </svg>
                  {Number(
                    data.previousMonthGeneration.toFixed(0)
                  ).toLocaleString() || 0}{" "}
                  MWh
                </Value>
              </GraphHeader>
              <Graph
                widthPercent={getWidthPercent(
                  data.previousMonthGeneration,
                  generationMax
                )}
              />
            </GraphItem>
            <GraphItem>
              <GraphHeader>
                <DateWrapper>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 4.06C13.93 2.73 12.81 1.75 11.48 1.75H11.2V0.7C11.2 0.28 10.92 0 10.5 0C10.08 0 9.8 0.28 9.8 0.7V1.75H4.2V0.7C4.2 0.28 3.92 0 3.5 0C3.08 0 2.8 0.28 2.8 0.7V1.75H2.52H2.38C1.05 1.82 0 2.94 0 4.27V5.67V5.95V11.55C0 12.88 1.12 14 2.45 14H11.55C12.88 14 14 12.88 14 11.55V5.95V5.67V4.27V4.06ZM12.95 11.55C12.95 12.32 12.32 12.95 11.55 12.95H2.45C1.68 12.95 1.05 12.32 1.05 11.55V5.95H12.95V11.55Z"
                      fill="#8C8C8C"
                    />
                  </svg>
                  {lastYearLastMonthFormatted}
                </DateWrapper>
                <Value>
                  <svg
                    width="11"
                    height="14"
                    viewBox="0 0 11 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.35394 0.5814C2.47589 0.232794 2.80002 0 3.16344 0H7.6001C8.18665 0 8.60082 0.585145 8.41534 1.15177L7.41743 4.20026H10.1389C10.898 4.20026 11.2843 5.12909 10.7561 5.68421L3.15205 13.6761C2.42764 14.4374 1.17768 13.7504 1.40517 12.7159L2.27772 8.74804L1.19946 8.74383C0.369863 8.74059 -0.207634 7.90344 0.0707689 7.10765L2.35394 0.5814Z"
                      fill="#8C8C8C"
                    />
                  </svg>
                  {Number(
                    data.lastYearPreviousMonthGeneration.toFixed(0)
                  ).toLocaleString() || 0}{" "}
                  MWh
                </Value>
              </GraphHeader>
              <Graph2
                widthPercent={getWidthPercent(
                  data.lastYearPreviousMonthGeneration,
                  generationMax
                )}
              />
            </GraphItem>
          </GraphWrapper>
        </Item>
      </CardFrame>

      <CardFrame>
        <Item>
          <TitleBadge>
            <Title>
              <svg
                width="28"
                height="19"
                viewBox="0 0 28 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 0.5C18.9291 0.5 21.7253 3.6472 22.132 7.44798L22.2564 7.44798C25.4285 7.44798 28 9.92206 28 12.974C28 16.0259 25.4285 18.5 22.2564 18.5H5.74359C2.57149 18.5 0 16.0259 0 12.974C0 9.92206 2.57149 7.44798 5.74362 7.44798L5.86799 7.44798C6.27702 3.62222 9.07087 0.5 14 0.5Z"
                  fill="#4282EF"
                />
                <path
                  d="M14.9659 14.5031C16.1838 14.5031 16.9655 13.5976 16.9655 12.4511C16.9655 11.313 16.1838 10.4074 14.9659 10.4074C13.7481 10.4074 12.9664 11.313 12.9664 12.4511C12.9664 13.5976 13.7481 14.5031 14.9659 14.5031ZM11.8555 12.4511C11.8555 10.7065 13.1227 9.26927 14.9659 9.26927C16.8091 9.26927 18.0763 10.7065 18.0763 12.4511C18.0763 14.204 16.8091 15.633 14.9659 15.633C13.1227 15.633 11.8555 14.204 11.8555 12.4511Z"
                  fill="white"
                />
                <path
                  d="M9.12183 15.633C7.26217 15.633 6.07726 14.1957 6.07726 12.4511C6.07726 10.7065 7.26217 9.26096 9.12183 9.26927C9.96937 9.26927 10.7346 9.6265 11.2777 10.1997L10.5618 11.0471C10.2245 10.7065 9.632 10.4905 9.12183 10.4905C7.93692 10.4822 7.19635 11.3628 7.19635 12.4511C7.19635 13.5477 7.93692 14.42 9.12183 14.42C9.632 14.42 10.2245 14.1957 10.5618 13.8551L11.2777 14.7025C10.7346 15.2757 9.96937 15.633 9.12183 15.633Z"
                  fill="white"
                />
                <path
                  d="M19.2082 14.8391C20.0146 14.0637 21.024 13.5486 21.024 12.9062C21.024 12.5406 20.7442 12.3302 20.4206 12.3302C20.2066 12.3302 19.9268 12.4077 19.7568 12.7898L19.0766 12.4797C19.4002 11.7486 20.0805 11.5769 20.4206 11.5769C21.0514 11.5769 21.77 12.1197 21.77 12.9062C21.77 13.7258 21.0459 14.3074 20.3712 14.8391H21.8249V15.5923H19.2137V14.8446L19.2082 14.8391Z"
                  fill="white"
                />
              </svg>
              온실가스 감축량
            </Title>
            <Badge>
              감축효과 나무{" "}
              {Number(
                (data.previousMonthReductions / 2.3 / 1000).toFixed(0)
              ).toLocaleString()}
              그루
            </Badge>
          </TitleBadge>
          <GraphWrapper>
            <GraphItem>
              <GraphHeader>
                <DateWrapper>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 4.06C13.93 2.73 12.81 1.75 11.48 1.75H11.2V0.7C11.2 0.28 10.92 0 10.5 0C10.08 0 9.8 0.28 9.8 0.7V1.75H4.2V0.7C4.2 0.28 3.92 0 3.5 0C3.08 0 2.8 0.28 2.8 0.7V1.75H2.52H2.38C1.05 1.82 0 2.94 0 4.27V5.67V5.95V11.55C0 12.88 1.12 14 2.45 14H11.55C12.88 14 14 12.88 14 11.55V5.95V5.67V4.27V4.06ZM12.95 11.55C12.95 12.32 12.32 12.95 11.55 12.95H2.45C1.68 12.95 1.05 12.32 1.05 11.55V5.95H12.95V11.55Z"
                      fill="#8C8C8C"
                    />
                  </svg>
                  {lastMonthFormatted}
                </DateWrapper>
                <Value>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.5 7C12.433 7 14 5.433 14 3.5C14 1.567 12.433 3.37976e-07 10.5 0C8.567 -3.37976e-07 7 1.567 7 3.5C7 4.04085 7.12268 4.55305 7.34172 5.01028L4.61099 6.37565C4.1334 6.03424 3.5485 5.83333 2.91667 5.83333C1.30584 5.83333 2.81647e-07 7.13917 0 8.75C-2.81646e-07 10.3608 1.30584 11.6667 2.91667 11.6667C4.03658 11.6667 5.00907 11.0355 5.4978 10.1094L8.17669 11.4489C8.17006 11.5206 8.16667 11.5932 8.16667 11.6667C8.16667 12.9553 9.21133 14 10.5 14C11.7887 14 12.8333 12.9553 12.8333 11.6667C12.8333 10.378 11.7887 9.33333 10.5 9.33333C9.70213 9.33333 8.9978 9.7338 8.57703 10.3447L5.82525 8.9688C5.83061 8.89656 5.83333 8.8236 5.83333 8.75C5.83333 8.20987 5.68651 7.70403 5.4306 7.27021L8.02385 5.97359C8.65732 6.60772 9.53285 7 10.5 7Z"
                      fill="#8C8C8C"
                    />
                  </svg>
                  {Number(
                    data.previousMonthReductions.toFixed(0)
                  ).toLocaleString() || 0}{" "}
                  tCO-2eq
                </Value>
              </GraphHeader>
              <Graph
                widthPercent={getWidthPercent(
                  data.previousMonthReductions,
                  reductionMax
                )}
              />
            </GraphItem>
            <GraphItem>
              <GraphHeader>
                <DateWrapper>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 4.06C13.93 2.73 12.81 1.75 11.48 1.75H11.2V0.7C11.2 0.28 10.92 0 10.5 0C10.08 0 9.8 0.28 9.8 0.7V1.75H4.2V0.7C4.2 0.28 3.92 0 3.5 0C3.08 0 2.8 0.28 2.8 0.7V1.75H2.52H2.38C1.05 1.82 0 2.94 0 4.27V5.67V5.95V11.55C0 12.88 1.12 14 2.45 14H11.55C12.88 14 14 12.88 14 11.55V5.95V5.67V4.27V4.06ZM12.95 11.55C12.95 12.32 12.32 12.95 11.55 12.95H2.45C1.68 12.95 1.05 12.32 1.05 11.55V5.95H12.95V11.55Z"
                      fill="#8C8C8C"
                    />
                  </svg>
                  {lastYearLastMonthFormatted}
                </DateWrapper>
                <Value>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.5 7C12.433 7 14 5.433 14 3.5C14 1.567 12.433 3.37976e-07 10.5 0C8.567 -3.37976e-07 7 1.567 7 3.5C7 4.04085 7.12268 4.55305 7.34172 5.01028L4.61099 6.37565C4.1334 6.03424 3.5485 5.83333 2.91667 5.83333C1.30584 5.83333 2.81647e-07 7.13917 0 8.75C-2.81646e-07 10.3608 1.30584 11.6667 2.91667 11.6667C4.03658 11.6667 5.00907 11.0355 5.4978 10.1094L8.17669 11.4489C8.17006 11.5206 8.16667 11.5932 8.16667 11.6667C8.16667 12.9553 9.21133 14 10.5 14C11.7887 14 12.8333 12.9553 12.8333 11.6667C12.8333 10.378 11.7887 9.33333 10.5 9.33333C9.70213 9.33333 8.9978 9.7338 8.57703 10.3447L5.82525 8.9688C5.83061 8.89656 5.83333 8.8236 5.83333 8.75C5.83333 8.20987 5.68651 7.70403 5.4306 7.27021L8.02385 5.97359C8.65732 6.60772 9.53285 7 10.5 7Z"
                      fill="#8C8C8C"
                    />
                  </svg>
                  {Number(
                    data.lastYearPreviousMonthReductions.toFixed(0)
                  ).toLocaleString() || 0}{" "}
                  tCO-2eq
                </Value>
              </GraphHeader>
              <Graph2
                widthPercent={getWidthPercent(
                  data.lastYearPreviousMonthReductions,
                  reductionMax
                )}
              />
            </GraphItem>
          </GraphWrapper>
        </Item>
      </CardFrame>
    </Wrapper>
  )
}

export default ComparisonGraph
